import { useEffect, useRef, useState } from 'react';
import Overlay from '../../ui/Overlay';

import { createBounty, getBounty } from '../../apis/bounties/bounties';

import ModalBox from '../../ui/ModalBox';
import { sanitizeBountyData } from '../../helpers/sanitizeBountyDetails';
import { useLoadingState } from '../../hooks/useLoader';
import { toast } from 'sonner';
import Loader from '../../ui/Loader';

function DuplicateBountyModal({ close, bountyName, bountyId, handleConfirmAction }) {
    const modalRef = useRef(null); // Reference for the modal content
    const { loading, startLoading, stopLoading } = useLoadingState();
    const { loading: duplicating, startLoading: startDuplicating, stopLoading: stopDuplicating } = useLoadingState();
    const [bountyPayload, setBountyPayload] = useState({});

    useEffect(() => {
        // Fetch the bounty data asynchronously
        const fetchData = async () => {
            try {
                startDuplicating();
                const data = await getBounty(bountyId);
                setBountyPayload(data);
            } catch (error) {
                console.error('Error fetching bounty:', error);
            } finally {
                stopDuplicating();
            }
        };
        fetchData();

        // Handle click outside the modal
        function handleClickOutside(e) {
            // Check if the click was outside the modal content
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                close(); // Close the modal if clicked outside
            }
        }

        // Add event listener in the capture phase to ensure proper handling
        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            // Clean up the event listener on unmount
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [close, bountyId]);

    return (
        <Overlay>
            <div
                ref={modalRef} // Attach ref to modal content
                className="absolute rounded-lg left-[50%]     bg-grayBgNormal translate-x-[-50%] translate-y-[-50%] top-[50%]"
                onClick={(e) => e.stopPropagation()} // Stop propagation to avoid closing the modal when clicked inside
            >
                {duplicating ? (
                    <div className="p-6 w-[518px] flex items-center justify-center h-[177.6px] ">
                        <Loader />
                    </div>
                ) : (
                    <ModalBox
                        close={close}
                        onSubmit={async () => {
                            const { __v, id, _id, createdAt, updatedAt, creatorUid, ...payload } = bountyPayload;
                            const sanitizedPayload = sanitizeBountyData(payload);

                            try {
                                startLoading();
                                const response = await createBounty(sanitizedPayload);

                                if (response?._id) toast.success('Bounty successfully duplicated. ');
                                handleConfirmAction(response);
                            } catch (error) {
                                console.log(error);
                            } finally {
                                close(); // Close modal after duplication
                                stopLoading();
                            }
                        }}
                        primaryBtnText={'Submit'}
                        loading={loading}
                        secondaryBtnText={'Cancel'}
                    >
                        <>
                            Are you sure you want to{' '}
                            <span className="font-bold">duplicate the bounty {bountyName} </span>?
                        </>
                    </ModalBox>
                )}
            </div>
        </Overlay>
    );
}

export default DuplicateBountyModal;
